<template>
	<TransitionRoot :show="settingsOpen" appear as="template">
		<Dialog
			class="fixed z-50 inset-0 overflow-y-auto"
			aria-labelledby="cookie-settings-title"
			role="dialog"
			aria-modal="true"
			@close="closeSettings"
		>
			<TransitionChild
				as="template"
				enter="duration-300 ease-out"
				enter-from="opacity-0"
				enter-to="opacity-100"
				leave="duration-200 ease-in"
				leave-from="opacity-100"
				leave-to="opacity-0"
			>
				<div class="fixed inset-0 z-50 bg-gray-500 bg-opacity-60" />
			</TransitionChild>
			<TransitionChild
				as="template"
				enter="duration-300 ease-out"
				enter-from="translate-y-full sm:opacity-0"
				enter-to="translate-y-0 sm:opacity-100"
				leave="duration-200 ease-in"
				leave-from="translate-y-0 sm:opacity-100"
				leave-to="translate-y-full sm:opacity-0"
			>
				<DialogPanel
					class="flex flex-col z-60 fixed overflow-auto bg-white sm:ring-1 sm:ring-gray-900/10 w-full sm:max-w-lg bottom-0 sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 h-max max-h-100vh sm:max-h-90vh"
				>
					<div class="p-5 sm:p-6">
						<h2
							id="cookie-settings-title"
							class="text-lg font-semibold text-gray-900 mb-4"
						>
							{{ t("cookie.modal.title") }}
						</h2>
						<div class="flex flex-col gap-4">
							<label
								v-for="cat in categoryDefs"
								:key="cat.key"
								class="flex items-start gap-3"
							>
								<input
									type="checkbox"
									class="mt-1"
									:checked="draft[cat.key]"
									:disabled="cat.key === 'necessary'"
									@change="onToggle(cat.key, $event)"
								/>
								<span class="flex-1">
									<span class="block text-sm font-medium text-gray-900">
										{{ t(`cookie.modal.${cat.key}_label`) }}
									</span>
									<span class="block text-xs text-gray-600">
										{{ t(`cookie.modal.${cat.key}_description`) }}
									</span>
								</span>
							</label>
						</div>
						<div class="mt-6 flex flex-col sm:flex-row gap-2 justify-end">
							<button
								type="button"
								class="px-4 py-2 text-sm font-medium border border-gray-300 rounded hover:bg-gray-50"
								@click="onReject"
							>
								{{ t("cookie.banner.reject_all") }}
							</button>
							<button
								type="button"
								class="px-4 py-2 text-sm font-medium border border-gray-300 rounded hover:bg-gray-50"
								@click="onAccept"
							>
								{{ t("cookie.banner.accept_all") }}
							</button>
							<button
								type="button"
								class="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded hover:bg-black"
								@click="onSave"
							>
								{{ t("cookie.modal.save") }}
							</button>
						</div>
					</div>
				</DialogPanel>
			</TransitionChild>
		</Dialog>
	</TransitionRoot>
</template>

<script setup lang="ts">
import { reactive, watch } from "vue";
import {
	Dialog,
	DialogPanel,
	TransitionRoot,
	TransitionChild
} from "@headlessui/vue";

const { t } = useI18n();
const {
	settingsOpen,
	categories,
	acceptAll,
	rejectAll,
	savePreferences,
	closeSettings
} = useCookieConsent();

type ToggleableKey = "statistics" | "marketing" | "preferences";

const categoryDefs: { key: "necessary" | ToggleableKey }[] = [
	{ key: "necessary" },
	{ key: "statistics" },
	{ key: "marketing" },
	{ key: "preferences" }
];

const draft = reactive({
	necessary: true,
	statistics: categories.value.statistics,
	marketing: categories.value.marketing,
	preferences: categories.value.preferences
});

watch(
	() => settingsOpen.value,
	(open) => {
		if (open) {
			draft.statistics = categories.value.statistics;
			draft.marketing = categories.value.marketing;
			draft.preferences = categories.value.preferences;
		}
	}
);

const onToggle = (key: "necessary" | ToggleableKey, e: Event) => {
	if (key === "necessary") return;
	const target = e.target as HTMLInputElement;
	draft[key] = target.checked;
};

const onSave = () => {
	savePreferences({
		statistics: draft.statistics,
		marketing: draft.marketing,
		preferences: draft.preferences
	});
};

const onAccept = () => acceptAll();
const onReject = () => rejectAll();
</script>
